import { FC, useState } from "react"
import Title from "antd/lib/typography/Title"
import { Button, InputNumber, Modal, Space, Switch } from "antd"
import { IExternalOfferProvider } from "../api/types"
import { updateExternalOfferProvider } from "../api"
import useExternalProviders from "../hooks/useExternalProviders"
import { CreateExternalProvider } from "./createExternalProvider"
import { paramStructuresByType, providerNamesByType } from "../constants/externalProviders"
import { DraggableTable, DragHandle, reorderPriorities } from "./DraggableTable"
import { notifications } from "notifications"

const InternalOWExternalProvidersPage: FC = () => {
  const { providers, updateProvider: updateProviderEntry } = useExternalProviders()
  const [editProvider, setEditProvider] = useState<IExternalOfferProvider | true>()
  const [patching, setPatching] = useState(false)

  const onPatch = (provider: IExternalOfferProvider) => {
    if (patching) return
    setPatching(true)
    updateExternalOfferProvider(provider).then(updated => {
      updateProviderEntry(updated)
    }).finally(() => {
      setPatching(false)
    })
  }

  const onPriorityChange = (provider: IExternalOfferProvider, newPriority: number | null) => {
    if (newPriority === null || newPriority === provider.priority || !providers) return
    if (patching) return
    setPatching(true)

    const oldPriority = provider.priority
    const updates: IExternalOfferProvider[] = []

    if (oldPriority < newPriority) {
      providers.forEach(p => {
        if (p.id === provider.id) return
        if (p.priority > oldPriority && p.priority <= newPriority) {
          updates.push({ ...p, priority: p.priority - 1 })
        }
      })
    } else {
      providers.forEach(p => {
        if (p.id === provider.id) return
        if (p.priority >= newPriority && p.priority < oldPriority) {
          updates.push({ ...p, priority: p.priority + 1 })
        }
      })
    }

    updates.push({ ...provider, priority: newPriority })

    Promise.all(updates.map(updateExternalOfferProvider))
      .then(updated => {
        updated.forEach(updateProviderEntry)
      })
      .finally(() => {
        setPatching(false)
      })
  }

  const onDragEnd = (activeId: string, overId: string, activeIndex: number, overIndex: number) => {
    if (patching || !providers) return
    setPatching(true)
    reorderPriorities(providers, activeIndex, overIndex, updateExternalOfferProvider, updateProviderEntry)
      .finally(() => setPatching(false))
  }

  const onResetPriorities = () => {
    if (patching || !providers) return

    const updates: IExternalOfferProvider[] = []
    providers.forEach((provider, index) => {
      const correctPriority = index + 1
      if (provider.priority === correctPriority) return

      updates.push({
        ...provider,
        priority: correctPriority,
      })
    })

    if (updates.length === 0) {
      notifications.success("Priorities are already correct")

      return
    }

    setPatching(true)
    Promise.all(updates.map(updateExternalOfferProvider))
      .then(updated => {
        updated.forEach(updateProviderEntry)
        notifications.success("Priorities reset successfully")
      })
      .catch(e => {
        notifications.error(e.cause?.message || "Failed to reset priorities")
      })
      .finally(() => {
        setPatching(false)
      })
  }

  const onClose = (updated?: IExternalOfferProvider) => {
    if (updated) {
      updateProviderEntry(updated)
    }
    setEditProvider(undefined)
  }

  return (
    <div>
      <Modal
        open={!!editProvider}
        title="Edit a Provider"
        width="700px"
        footer={null}
        onCancel={() => onClose()}
        destroyOnClose
        style={{ top: "10px" }}
      >
        <CreateExternalProvider onClose={onClose} initial={editProvider !== true ? editProvider : undefined} />
      </Modal>
      <Space
        direction="horizontal"
        style={{
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: 12,
        }}
      >
        <Title level={2}>External Providers</Title>
        <Button
          onClick={onResetPriorities}
          disabled={patching || !providers || providers.length === 0}
        >
          Reset Priorities
        </Button>
      </Space>
      <Space direction="vertical" style={{ width: "100%" }}>
        <DraggableTable<IExternalOfferProvider>
          dataSource={providers}
          rowKey={record => record.id}
          scroll={{ x: true }}
          loading={!providers}
          onDragEnd={onDragEnd}
          onRow={record => ({
            style: { cursor: "pointer" },
            onClick: () => {
              setEditProvider(record)
            },
          })}
          columns={[
            { key: "name", title: "Title", dataIndex: "name" },
            {
              key: "type",
              title: "Provider Type",
              dataIndex: "type",
              render: type => providerNamesByType[type],
            },
            {
              key: "regions",
              title: "Regions",
              dataIndex: "geoRegions",
              render: regions => regions.join(", "),
            },
            {
              key: "params",
              title: "Params",
              dataIndex: "params",
              render: (params, { type }) => {
                const structure = paramStructuresByType[type]?.definition
                if (!structure) {
                  return "No params"
                }

                return Object.entries(params).map(([key, value]) => {
                  const keyStructure = structure[key]
                  const labelInStructure = keyStructure?.label
                  if (!labelInStructure) {
                    return null
                  }

                  let paramValue = params[key]
                  if (keyStructure?.type === "select") {
                    paramValue = keyStructure.options?.find(({ value: v }) => value === v)?.title
                  }

                  return (
                    <div key={key}>
                      <b>{structure[key].label}</b>: {paramValue}
                    </div>
                  )
                })
              },
            },
            {
              key: "active",
              title: "Active",
              dataIndex: "active",
              render: (active, provider) => (
                <div onClick={e => e.stopPropagation()}>
                  <Switch
                    onClick={() => onPatch({ ...provider, active: !provider.active })}
                    disabled={patching}
                    checked={active}
                  />
                </div>
              ),
            },
            {
              key: "priority",
              title: "Priority",
              dataIndex: "priority",
              render: (priority, provider) => (
                <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center" }}>
                  <DragHandle />
                  <InputNumber
                    value={priority}
                    min={1}
                    disabled={patching}
                    controls={false}
                    style={{ width: 60 }}
                    onBlur={e => onPriorityChange(provider, parseInt(e.target.value, 10) || priority)}
                    onPressEnter={e => onPriorityChange(provider, parseInt((e.target as HTMLInputElement).value, 10) || priority)}
                  />
                </div>
              ),
            },
          ]}
        />
      </Space>
    </div>
  )
}

export default InternalOWExternalProvidersPage
